import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileUpload } from '../components/FileUpload';
import type { AnalysisResults } from '@printability/shared-types';

export const Upload: React.FC = () => {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleAnalysisComplete = (analysis: {
    analysisId: string;
    filename: string;
    results: AnalysisResults;
    processingTime: number;
    fileData: ArrayBuffer;
  }) => {
    setError(null);
    setSuccess(`Analysis complete for ${analysis.filename}! Processing time: ${analysis.processingTime}ms`);

    // Navigate to analysis results page
    // Store the results and file data in sessionStorage
    sessionStorage.setItem('latestAnalysis', JSON.stringify({
      analysisId: analysis.analysisId,
      filename: analysis.filename,
      results: analysis.results,
      processingTime: analysis.processingTime,
    }));

    // Store file data separately (can't JSON stringify ArrayBuffer)
    sessionStorage.setItem('latestFileData', JSON.stringify(Array.from(new Uint8Array(analysis.fileData))));

    navigate(`/analysis/${analysis.analysisId}`);
  };

  const handleError = (errorMessage: string) => {
    setError(errorMessage);
    setSuccess(null);
  };

  // Enhanced error display for complex models
  const renderError = (error: string) => {
    // Check if this is a complex model error with recommendations
    if (error.includes('Recommendations:') || error.includes('Suggestions:')) {
      const parts = error.split(/(?:Recommendations:|Suggestions:)/);
      const mainError = parts[0].trim();
      const recommendations = parts[1]?.trim();

      return (
        <div className="rounded-md bg-red-50 p-4 border border-red-200">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3 flex-1">
              <h3 className="text-sm font-medium text-red-800">Model Too Complex</h3>
              <div className="mt-2 text-sm text-red-700">
                <p className="whitespace-pre-line">{mainError}</p>
                {recommendations && (
                  <div className="mt-3">
                    <p className="font-medium">How to fix this:</p>
                    <div className="mt-1 whitespace-pre-line">{recommendations}</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Regular error display
    return (
      <div className="rounded-md bg-red-50 p-4">
        <div className="text-sm text-red-700">{error}</div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Upload 3D Model</h1>
        <p className="mt-1 text-sm text-gray-500">
          Upload your 3D model files for comprehensive printability analysis.
        </p>
      </div>

      {/* Error Message */}
      {error && renderError(error)}

      {/* Success Message */}
      {success && (
        <div className="rounded-md bg-green-50 p-4">
          <div className="text-sm text-green-700">{success}</div>
        </div>
      )}

      {/* File Upload Component */}
      <div className="card">
        <FileUpload
          onAnalysisComplete={handleAnalysisComplete}
          onError={handleError}
        />
      </div>

      {/* Help Section */}
      <div className="card">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          How it works
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-3">
              <span className="text-blue-600 font-bold text-lg">1</span>
            </div>
            <h4 className="font-medium text-gray-900 mb-2">Upload Your Model</h4>
            <p className="text-sm text-gray-500">
              Drag and drop or select your STL or OBJ file. Files up to 50MB are supported.
            </p>
          </div>

          <div className="text-center">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-3">
              <span className="text-blue-600 font-bold text-lg">2</span>
            </div>
            <h4 className="font-medium text-gray-900 mb-2">AI Analysis</h4>
            <p className="text-sm text-gray-500">
              Our advanced algorithms analyze your model for printability issues and optimization opportunities.
            </p>
          </div>

          <div className="text-center">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-3">
              <span className="text-blue-600 font-bold text-lg">3</span>
            </div>
            <h4 className="font-medium text-gray-900 mb-2">Get Results</h4>
            <p className="text-sm text-gray-500">
              Receive detailed analysis results with actionable recommendations to improve your print success.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
